




import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import vue from 'vue';
import {colors} from '@/config';

More(Highcharts);

@Component({components: {Chart}})
export default class DynamicColumnChart extends Vue {
  @Prop() profitloss: any;
  @Prop() balance: any;
  @Prop() cashflow: any;
  @Prop() valuation: any;
  @Prop() financialRatio: any;

  @Prop() dark!: boolean;

  chartOptions: any = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
    },

    credits: {
      enabled: false,
    },

    title: {
      text: '',
    },

    xAxis: {
      type: 'category',
      labels: {
        autoRotation: [0],
        staggerLines: 1,
        padding: 10,
        style: {
          fontSize: this.dark ? '1.25rem' : '1rem',
          textOverflow: 'none',
          whiteSpace: 'normal',
          color: this.dark ? colors.Light : colors.Black,
        },
      },
    },

    yAxis: {
      title: {
        text: '',
      },
      labels: {
        style: {
          fontSize: this.dark ? '1.25rem' : '1rem',
          color: this.dark ? colors.Light : colors.Black,
          fontWeight: this.dark ? 400 : 600,
        },
        formatter() {
          return (vue as any).options.filters.KR((this as any).value);
        },
      },
      tickPixelInterval: this.dark ? 250 : 175,
      gridLineColor: this.dark ? colors.Black : colors.LightGrey,
      plotLines: [{
        color: colors.Black,
        width: this.dark ? 3 : 2,
        value: 0,
        zIndex: 20,
      }],
      softMin: 0,
    },

    legend: {
      verticalAlign: 'top',
      align: 'right',
      enabled: true,
      itemStyle: {
        fontSize: this.dark ? '1.25rem' : '1rem',
        fontWeight: 'normal',
        color: this.dark ? colors.Light : colors.Black,
      },
      itemHoverStyle: {
        color: this.dark ? colors.Light : colors.Black,
      },
      symbolWidth: 25,
    },

    tooltip: {
      headerFormat: '',
      pointFormat: '<span>{point.name}: <b>{point.y:,.0f} tkr</b></span>',
      style: {
        fontSize: '1.25rem',
        color: this.dark ? colors.Light : colors.Black,
      },
      backgroundColor: this.dark ? colors.Dark : colors.Light,
    },

    series: [],
  };

  @Watch('profitloss', {immediate: true, deep: true})
  profitlossChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('balance', {immediate: true, deep: true})
  balanceChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('cashflow', {immediate: true, deep: true})
  cashflowChanged() {
    this.chartOptions.series = this.getData();
  }

  getData(): any {
    switch (this.financialRatio) {
      case 'turnover':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Secondary : colors.Primary,
          pointPadding: 0,
          borderColor: this.dark ? colors.Black : colors.Black,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: p.A,
            // tslint:disable:max-line-length
            color: this.dark ? (i === 0 ? colors.Tertiary : colors.Secondary) : (i === 0 ? colors.Secondary : colors.Primary),
          })).reverse(),
        }];
      case 'operatingResult':
        if (!this.profitloss) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.profitloss.sheets.map((p: any, i: number) => ({
            name: p.DATE_TO.slice(5, 7) === '12' ? p.DATE_TO.slice(0, 4) : p.DATE_TO.slice(0, 7),
            y: p.K,
            color: i === 0 ? colors.Secondary : colors.Primary,
          })).reverse(),
        }];
      case 'totalAssets':
        if (!this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Secondary : colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: b.L,
            // tslint:disable:max-line-length
            color: this.dark ? (i === 0 ? colors.Tertiary : colors.Secondary) : (i === 0 ? colors.Secondary : colors.Primary),
          })).reverse(),
        }];
      case 'operatingCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.OperatingCashflow,
            color: i === 0 ? colors.Secondary : colors.Primary,
          })).reverse(),
        }];
      case 'cash':
        if (!this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Secondary : colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: b.J,
            // tslint:disable:max-line-length
            color: this.dark ? (i === 0 ? colors.Tertiary : colors.Secondary) : (i === 0 ? colors.Secondary : colors.Primary),
          })).reverse(),
        }];
      case 'workingCapital':
        if (!this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: b.K - b.J - b.Q + b.U,
            color: i === 0 ? colors.Secondary : colors.Primary,
          })).reverse(),
        }];
      case 'workingCapitalDelta':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Positive,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: -c.WorkingCaptialCashflow,
            color: c.WorkingCaptialCashflow > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'investmentsCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Negative,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.InvestmentsCashflow,
            color: c.InvestmentsCashflow > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'totalCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Positive,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.CashFlow,
            color: c.CashFlow > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'interestBearingLiabilities':
        if (!this.balance) {
          return [];
        }
        return [{
          name: this.getName(),
          color: this.dark ? colors.Secondary : colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.balance.sheets.map((b: any, i: number) => ({
            name: b.DATE_TO.slice(5, 7) === '12' ? b.DATE_TO.slice(0, 4) : b.DATE_TO.slice(0, 7),
            y: b.U + b.P,
            // tslint:disable:max-line-length
            color: this.dark ? (i === 0 ? colors.Tertiary : colors.Secondary) : (i === 0 ? colors.Secondary : colors.Primary),
          })).reverse(),
        }];
      case 'interestBearingLiabilitiesDelta':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Negative,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.InterestLiabilitiesDelta,
            color: c.InterestLiabilitiesDelta > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'ownersCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Negative,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.EquityWithoutNetProfitDelta + c.SubscribedCapitalUnpaidDelta,
            // tslint:disable:max-line-length
            color: c.EquityWithoutNetProfitDelta + c.SubscribedCapitalUnpaidDelta > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'financingCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Negative,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.FinancingCashflow,
            color: c.FinancingCashflow > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'operatingCashflowPosNeg':
        if (!this.cashflow) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Positive,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.cashflow.sheets.map((c: any, i: number) => ({
            name: c.DATE_TO.slice(5, 7) === '12' ? c.DATE_TO.slice(0, 4) : c.DATE_TO.slice(0, 7),
            y: c.OperatingCashflow,
            color: c.OperatingCashflow > 0 ? colors.Positive : colors.Negative,
          })).reverse(),
        }];
      case 'forecastNetsales':
        if (!this.valuation) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.valuation.FORECAST_NET_SALES.map((v: any, i: number) => ({
          name: parseInt(this.valuation.YEAR[0], 0) + 1 + i + 'P',
          y: v,
          })),
        }];
      case 'forecastOperationalCashflow':
        if (!this.valuation) {
          return [];
        }
        return [{
          name: this.getName(),
          color: colors.Primary,
          pointPadding: 0,
          borderColor: colors.Black,
          data: this.valuation.FORECAST_OPERATIONAL_CASHFLOW.map((v: any, i: number) => ({
          name: parseInt(this.valuation.YEAR[0], 0) + 1 + i + 'P',
          y: v,
          })),
        }];
    }
  }

  getName() {
    switch (this.financialRatio) {
      case 'turnover':
        return Vue.prototype.$t('Nettoomsättning');
      case 'operatingResult':
        return Vue.prototype.$t('Rörelseresultat');
      case 'totalAssets':
        return Vue.prototype.$t('Balansomslutning');
      case 'operatingCashflow':
        return Vue.prototype.$t('Operativa kassaflöden');
      case 'cash':
        return Vue.prototype.$t('Kassa & bank');
      case 'workingCapital':
        return Vue.prototype.$t('Bindning i rörelsekapital (exkl. kassa & bank)');
      case 'workingCapitalDelta':
        return Vue.prototype.$t('Förändring av bindning i rörelsekapital (exkl. kassa & bank)');
      case 'investmentsCashflow':
        return Vue.prototype.$t('Kassaflöde investeringar');
      case 'totalCashflow':
        return Vue.prototype.$t('Totalt kassaflöde');
      case 'interestBearingLiabilities':
        return Vue.prototype.$t('Räntebärande skulder');
      case 'interestBearingLiabilitiesDelta':
        return Vue.prototype.$t('Förändring av räntebärande skulder');
      case 'ownersCashflow':
        return Vue.prototype.$t('Ägarkapital');
      case 'financingCashflow':
        return Vue.prototype.$t('Kassaflöde avseende finansiering');
      case 'operatingCashflowPosNeg':
        return Vue.prototype.$t('Operativa kassaflöden');
      case 'forecastNetsales':
        return Vue.prototype.$t('Prognos nettoomsättning');
      case 'forecastOperationalCashflow':
        return Vue.prototype.$t('Prognos operativa kassaflöden');
    }
  }
}
